import { orderBy } from 'lodash';
import {
    IPaymentGatewayView,
    IRestaurant,
    IRestaurantOrderConfig,
    IRestaurantPaymentGateway,
    IRestaurantQRMeta,
    OrderModeEnum,
    OrderPaymentMethodEnum,
} from './type';
import { parseJSON } from '../../utility/k_json';
import { fallbackArray } from '../../utility/k_array';
import { elemOrders, gatewayMap, PaymentTypeEnum, PGNameEnum } from '../common/payment/type';
import { changeDomain } from '../../utility/k_url';
import { cdnDomain } from '../axios/constants';
import { QSRQrModeEnum } from '../../hook/router';

export const parsePaymentGateway = (gateway: IRestaurantPaymentGateway): IPaymentGatewayView => {
    const getType = () => {
        return gatewayMap[gateway.name.toLowerCase() as PGNameEnum] || PaymentTypeEnum.None;
    };
    const type = getType();
    return {
        id: gateway.id,
        name: gateway.name.toLowerCase(),
        type,
        config: {
            ...gateway.config,
            pgElemOrder: fallbackArray(gateway.config?.pgElemOrder, elemOrders),
            pgDefaultSelectedElem: fallbackArray(gateway.config?.pgDefaultSelectedElem, elemOrders),
            ...(type === PaymentTypeEnum.Tuna ? { pgGroup: true } : {}),
        },
    };
};

export const getAvailablePGs = (paymentGateways?: IRestaurantPaymentGateway[]) => {
    return paymentGateways?.map((o) => o.name).join('-');
};

export const convertMapType = (data: string): any | { [key: string]: string } => {
    const parsedObj = parseJSON(data);
    if (parsedObj && Array.isArray(parsedObj)) {
        return parsedObj.reduce((acc, curr) => {
            acc[curr.key] = curr.value;
            return acc;
        }, {});
    }
    return data;
};

export const parseVendor = (data: IRestaurant, qrMode: QSRQrModeEnum | undefined): IRestaurant => {
    const itemsList = orderBy(
        data?.paymentGateways || [],
        (item) => (item?.config?.pgGroup ? item?.config?.pgOrder || 0 : item?.config?.order || 0),
        ['asc'],
    ).map((pg) => {
        return parsePaymentGateway(pg as IRestaurantPaymentGateway);
    });

    const getCDN = (): Partial<IRestaurant> => {
        if (!cdnDomain) {
            return {};
        }
        return {
            backgroundUrl: changeDomain(data.backgroundUrl, cdnDomain),
            logoSmallUrl: changeDomain(data.logoSmallUrl, cdnDomain),
            logoBigUrl: changeDomain(data.logoBigUrl, cdnDomain),
            ...(data?.config?.useCDNInMenu ? { externalMenuUrl: changeDomain(data.externalMenuUrl, cdnDomain) } : {}),
            // externalMenuUrl: changeDomain(data.externalMenuUrl, cdnDomain),
        };
    };

    const res: IRestaurant = {
        ...data,
        paymentGatewayList: {
            total: data.paymentGateways?.length || 0,
            itemsList,
            hasPgGroup: itemsList.some((item) => item?.config?.pgGroup),
        },
        orderConfig:
            qrMode === QSRQrModeEnum.MenuOnly
                ? ({
                      menuItemCellV2: data.orderConfig?.menuItemCellV2,
                      menuViewDefault: data.orderConfig?.menuViewDefault,
                  } as IRestaurantOrderConfig)
                : ({
                      ...data.orderConfig,
                      paymentMethod: data.orderConfig?.paymentMethod || OrderPaymentMethodEnum.Default,
                      statusTitle: data.orderConfig?.statusTitle
                          ? convertMapType(data.orderConfig?.statusTitle as any)
                          : undefined,
                      diningOptionCustomText: data.orderConfig?.diningOptionCustomText
                          ? convertMapType(data.orderConfig?.diningOptionCustomText as any)
                          : undefined,
                      statusDesc: data.orderConfig?.statusDesc
                          ? convertMapType(data.orderConfig?.statusDesc as any)
                          : undefined,
                      customerExtraFields: data.orderConfig?.customerExtraFields
                          ? convertMapType(data.orderConfig?.customerExtraFields as any)
                          : undefined,
                  } as IRestaurantOrderConfig),
        orderMode: qrMode === QSRQrModeEnum.MenuOnly ? OrderModeEnum.DigitalMenu : data.orderMode,
        config: {
            ...data.config,
            ...(data.config.tipRoundingCRV ? { tipRoundingCRV: parseFloat(String(data.config.tipRoundingCRV)) } : {}),
            ...(data.config.tipRoundingMTC ? { tipRoundingMTC: parseFloat(String(data.config.tipRoundingMTC)) } : {}),
        },
        availablePGs: getAvailablePGs(data.paymentGateways),
        ...getCDN(),
    };
    if (res.posVendor) {
        if (res.posVendor.config?.hasOwnProperty('billLayout')) {
            res.posVendor.billLayout = parseJSON(res.posVendor.config.billLayout);
        }
        if (res.posVendor.config?.hasOwnProperty('itemLayout')) {
            res.posVendor.itemLayout = parseJSON(res.posVendor.config.itemLayout);
        }
    }
    if (res.meta) {
        res.metaObject = parseJSON(res.meta) as IRestaurantQRMeta;
    }
    return res;
};
